<!--
	@component
	Generates a tooltip that works on multiseries datasets, like multiline charts. It creates a tooltip showing the name of the series and the current value. This version uses percentages so you can use it to render server-side. It finds the nearest data point using the [QuadTree.percent-range.html.svelte](https://layercake.graphics/components/QuadTree.percent-range.html.svelte) component.
 -->
<script>
	import { getContext } from "svelte";
	import { format } from "d3-format";
	import { fade, blur, fly, slide, scale } from "svelte/transition";

	import QuadTree from "./QuadTree.svelte";
	import { animationParameters } from "../constants";

	import arrowLeft from "../icons/tooltip_arrow_left.svg";
	import arrowMid from "../icons/tooltip_arrow_mid.svg";
	import arrowRight from "../icons/tooltip_arrow_right.svg";

	const { data, width, x, y, z, xScale, config, xGet, yGet, zGet } = getContext("LayerCake");

	// /** @type {Function} [formatTitle=d => d] – A function to format the tooltip title, which is `$config.x`. */
	// export let formatTitle = (d) => d;

	// /** @type {Function} [formatKey=d => titleCase(d)] – A function to format the series name. */
	// export let formatKey = (d) => titleCase(d);

	// /** @type {Function} [formatValue=d => isNaN(+d) ? d : commas(d)] – A function to format the value. */
	// export let formatValue = (d) => (isNaN(+d) ? d : commas(d));

	/** @type {Number} [offset=-20] – A y-offset from the hover point, in pixels. */
	export let offset = -5;

	/** @type {Array} [dataset] – The dataset to work off of—defaults to $data if left unset. You can pass something custom in here in case you don't want to use the main data or it's in a strange format. */
	export let dataset = undefined;

	export let isValid = (d) => true;

	export let d;
	let found;
	let visible;

	$: d = visible && found ? found : null;

	const w = 80;
	const w2 = w / 2;
	let top = 0;

	/* --------------------------------------------
	 * Sort the keys by the highest value
	 */
	function sortResult(result) {
		if (Object.keys(result).length === 0) return [];
		const rows = Object.keys(result)
			.filter((d) => d !== $config.x)
			.map((key) => {
				return {
					key,
					value: result[key],
					data: result,
				};
			})
			.sort((a, b) => b.value - a.value);

		return rows;
	}

	$: isBandwidth = typeof $xScale.bandwidth === "function";

	$: top = Math.min(...$data.map((d) => $yGet(d)));
</script>

<QuadTree
	dataset={dataset || $data}
	y="x"
	let:x={xPos}
	let:y={yPos}
	bind:visible
	let:visible
	bind:found
	let:found
	let:e
>
	{@const foundSorted = sortResult(found)}
	{@const xValue = $x(found)}
	{@const yValue = $y(found)}
	{@const zValue = $z(found)}
	{@const xResult = $xGet(found)}
	{@const yResult = $yGet(found)}
	{@const zResult = $zGet(found)}
	{#if visible === true && isValid(found)}
		{@const left = Math.min(Math.max(w2, (xPos / 100) * $width), $width - w2)}
		{@const lefta = (xPos / 100) * $width - left + w2}
		{@const classa = lefta < 14 ? "left" : lefta > w - 14 ? "right" : "mid"}
		{@const a = (classa === "left" ? arrowLeft : classa === "right" ? arrowRight : arrowMid).replace(
			/(\r\n|\n|\r)/gm,
			""
		)}
		<div
			class="tooltip"
			transition:scale={animationParameters}
			style:width="{w}px"
			style:top="calc({top}% + {offset}px)"
			style:left="{left}px"
		>
			<slot d={found} {xValue} {yValue} {zValue} {xResult} {yResult} {zResult} />
			<div class="tooltip-arrow {classa}" style="left: {lefta}px">
				<img src={a} alt="" />
			</div>
		</div>
		<div
			class="tooltip-line"
			transition:scale={{ ...animationParameters, start: 0 }}
			style="left: calc({xResult}% + {isBandwidth ? 0.5 * $xScale.bandwidth() : 0}%)"
		/>
	{/if}
</QuadTree>

<style lang="scss">
	@import "../main.scss";
	.tooltip {
		position: absolute;
		pointer-events: none;
		background: #fff;
		transform: translate(calc(-50% - 0.5px), -100%);
		padding: 0;
		z-index: 1;
		border-radius: 5px;
		border: 1px solid #d9d9d9;
		box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.15);
	}
	.tooltip-line {
		width: 1px;
		transform: translateX(-50%);
		background: #d9d9d9;
		position: absolute;
		top: 0;
		z-index: -1;
	}

	// > small tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) {
		.tooltip-line {
			height: calc(100% + #{$offset-timeline});
		}
	}

	// <= small tablets
	@media (max-width: $breakpoint-tablet-s) {
		.tooltip-line {
			height: calc(100% + #{$offset-timeline-mobile});
		}
	}

	.tooltip-arrow {
		border-radius: 3px;
		top: calc(100% - 0.5px);
		left: 50%;
		position: absolute;
		transform-origin: center;

		&.mid {
			width: 12px;
			transform: translateX(-50%);
		}

		&.left {
			width: 7px;
			transform: translateX(calc(-50% + 3px));
		}

		&.right {
			width: 7px;
			transform: translateX(calc(-50% - 4px));
		}

		img {
			display: block;
		}
	}

	.tooltip.invert {
		transform: translate(-50%, 0);
	}

	.invert .tooltip-arrow {
		bottom: calc(100% - 0.5px);
		top: auto;

		&.mid {
			transform: translateX(-50%) scaleY(-1);
		}

		&.left {
			transform: translateX(calc(-50% + 3px)) scaleY(-1);
		}

		&.right {
			transform: translateX(calc(-50% - 4px)) scaleY(-1);
		}

		img {
			display: block;
		}
	}

	.line {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 1px;
		border-left: 1px dotted #666;
		pointer-events: none;
	}
	.tooltip,
	.line {
		transition: left 20ms ease-out, top 20ms ease-out;
	}
	.title {
		font-weight: bold;
	}
	.key {
		color: #999;
	}
</style>
